import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { UserRole } from '../types'
import { Shield, AlertCircle } from 'lucide-react'

interface RoleBasedRouteProps {
  children: React.ReactNode
  allowedRoles: UserRole[]
  fallback?: React.ReactNode
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  allowedRoles,
  fallback
}) => {
  const { userProfile, loading } = useAuth()
  const { t } = useLocale()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('access.checkingPermissions')}</p>
        </div>
      </div>
    )
  }

  if (!userProfile || !allowedRoles.includes(userProfile.role)) {
    if (fallback) {
      return <>{fallback}</>
    }

    return (
      <div className="max-w-2xl mx-auto mt-12 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('access.deniedTitle')}</h2>
          <p className="text-gray-600 mb-6">
            {t('access.deniedMessage')}{' '}
            {userProfile?.role === 'user'
              ? t('access.deniedAdminOnly')
              : t('access.deniedContact')
            }
          </p>
          <div className="flex items-center justify-center text-sm text-gray-500">
            <Shield className="w-4 h-4 mr-2" />
            {t('access.currentRole')}: {userProfile?.role || 'Unknown'}
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default RoleBasedRoute
